import { useState, useEffect, useCallback } from "react";
import { randomEvents } from "../../../mocks/dashboard";

interface PendingEvent {
  id: string;
  eventIndex: number;
  headline: string;
  type: string;
  savedAt: string;
  discussRoles: string[];
}

const ROLE_LABEL: Record<string, string> = {
  AR: "A&R", Finance: "재무", Marketing: "마케팅", Legal: "법무",
};

interface Props {
  onReopen: (eventIdx: number) => void;
}

export default function PendingEventsPanel({ onReopen }: Props) {
  const [pending, setPending] = useState<PendingEvent[]>([]);

  const load = useCallback(() => {
    try {
      const raw = localStorage.getItem("kap_pending_events");
      if (raw) setPending(JSON.parse(raw) as PendingEvent[]);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    // storage 이벤트로 실시간 반영
    const handler = () => load();
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [load]);

  const handleDecide = (item: PendingEvent) => {
    onReopen(item.eventIndex);
  };

  const handleDismiss = (id: string) => {
    const next = pending.filter(p => p.id !== id);
    setPending(next);
    localStorage.setItem("kap_pending_events", JSON.stringify(next));
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (pending.length === 0) return null;

  return (
    <div className="bg-[#111112] border border-amber-500/20 rounded-xl p-4 mb-4 animate-[fadeIn_0.4s_ease]">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-time-line text-amber-400/80 text-sm"></i>
        </div>
        <span className="text-amber-400/80 text-xs font-semibold">팀 협의 중 — 미결 이벤트</span>
        <span className="ml-auto text-[10px] bg-amber-400/10 text-amber-400/70 border border-amber-400/20 px-1.5 py-0.5 rounded-full font-medium">
          {pending.length}건 대기
        </span>
      </div>

      <div className="space-y-2">
        {pending.map((item) => {
          const event = randomEvents[item.eventIndex];
          const isCrisis = item.type === "crisis";
          return (
            <div
              key={item.id}
              className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.05] rounded-xl px-3.5 py-3"
            >
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${
                isCrisis ? "bg-red-400/10" : "bg-[#4B6CF7]/10"
              }`}>
                <i className={`text-sm ${isCrisis ? "ri-alert-line text-red-400/80" : "ri-lightbulb-line text-[#7A9BFF]"}`}></i>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-[#CCCCCC] text-[11px] font-medium truncate">{item.headline}</p>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="text-[#555555] text-[9px]">{formatTime(item.savedAt)}</span>
                  <span className="text-[#444444]">·</span>
                  <span className="text-[#555555] text-[9px]">협의 요청: {item.discussRoles.map(r => ROLE_LABEL[r] ?? r).join(", ")}</span>
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => handleDecide(item)}
                  className="text-[10px] px-2.5 py-1.5 rounded-lg bg-amber-400/15 text-amber-400/90 border border-amber-400/20 hover:bg-amber-400/25 transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  최종 결정
                </button>
                <button
                  type="button"
                  onClick={() => handleDismiss(item.id)}
                  title="미결 목록에서 제거"
                  className="w-6 h-6 flex items-center justify-center text-[#555555] hover:text-[#888888] transition-colors cursor-pointer rounded-lg hover:bg-white/[0.04]"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[#555555] text-[10px] mt-2.5 leading-relaxed">
        인박스에서 팀원 의견을 확인한 후 &quot;최종 결정&quot; 버튼을 눌러 대응 방식을 선택하세요.
      </p>
    </div>
  );
}
